import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';

interface SessionExpiredDialogProps {
  open: boolean;
  countdown: number;
  onConfirm: () => void;
}

/**
 * Session expired dialog component
 *
 * Shows when the user's session has expired
 * with a 5-second countdown before auto-redirect to login
 */
export const SessionExpiredDialog: React.FC<SessionExpiredDialogProps> = ({
  open,
  countdown,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderTop: 4,
          borderColor: 'error.main',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorIcon color="error" />
          <Typography variant="h6" component="span">
            세션 만료
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 2,
          }}
        >
          <Typography variant="body1" gutterBottom textAlign="center" sx={{ mb: 3 }}>
            로그인 제한시간이 만료되었습니다.
          </Typography>

          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              mb: 2,
            }}
          >
            <CircularProgress
              variant="determinate"
              value={(countdown / 5) * 100}
              size={80}
              thickness={4}
              sx={{ color: 'error.main' }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h4" component="span" color="error">
                {countdown}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            {countdown}초 후 자동으로 로그인 페이지로 이동합니다.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
        <Button onClick={onConfirm} variant="contained" color="primary" size="large" fullWidth>
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};
